import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CustomSkill,
  CreateCustomSkillParams,
  UpdateCustomSkillParams,
  ListCustomSkillsResponseSchema,
  CustomSkillResponseSchema,
  DeleteCustomSkillResponseSchema,
} from "@forgepoint/shared";

interface SkillLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface CustomSkillsStore {
  skills: Map<string, CustomSkill>;
  skillLoadingStates: Map<string, SkillLoadingState>;
  listLoading: boolean;
  listError: string | null;

  getSkill: (id: string) => CustomSkill | undefined;
  getSkillLoadingState: (id: string) => SkillLoadingState;
  fetchSkills: () => Promise<string[]>;
  createSkill: (params: CreateCustomSkillParams) => Promise<CustomSkill | null>;
  updateSkill: (
    skillId: string,
    params: UpdateCustomSkillParams,
  ) => Promise<CustomSkill | null>;
  deleteSkill: (
    skillId: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

const getInitialLoadingState = (): SkillLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

export const useCustomSkillsStore = create<CustomSkillsStore>((set, get) => ({
  skills: new Map(),
  skillLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  getSkill: (id: string) => get().skills.get(id),

  getSkillLoadingState: (id: string) =>
    get().skillLoadingStates.get(id) || getInitialLoadingState(),

  fetchSkills: async () => {
    set(() => ({ listLoading: true, listError: null }));

    try {
      const response = await fetchApi("custom-skills", "GET");

      if (response.ok && response.data) {
        const data = ListCustomSkillsResponseSchema.parse(response.data);
        set((state) => {
          const skillsMap = new Map(state.skills);
          data.customSkills.forEach((skill) => {
            skillsMap.set(skill.id, skill);
          });
          return { skills: skillsMap, listLoading: false };
        });
        return data.customSkills.map((skill) => skill.id);
      }

      set(() => ({
        listError: "Failed to fetch custom skills",
        listLoading: false,
      }));
      return [];
    } catch (error) {
      captureException(error as Error);
      set(() => ({
        listError: "Failed to fetch custom skills",
        listLoading: false,
      }));
      return [];
    }
  },

  createSkill: async (params: CreateCustomSkillParams) => {
    try {
      const response = await fetchApi("custom-skills", "POST", {
        body: params,
      });

      if (response.ok && response.data) {
        const result = CustomSkillResponseSchema.parse(response.data);
        set((state) => {
          const skillsMap = new Map(state.skills);
          skillsMap.set(result.customSkill.id, result.customSkill);
          return { skills: skillsMap };
        });
        return result.customSkill;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  updateSkill: async (skillId: string, params: UpdateCustomSkillParams) => {
    set((state) => {
      const loadingStates = new Map(state.skillLoadingStates);
      const currentState =
        loadingStates.get(skillId) || getInitialLoadingState();
      loadingStates.set(skillId, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { skillLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`custom-skills/${skillId}`, "PUT", {
        body: params,
      });

      if (response.ok && response.data) {
        const result = CustomSkillResponseSchema.parse(response.data);
        set((state) => {
          const skillsMap = new Map(state.skills);
          const loadingStates = new Map(state.skillLoadingStates);
          const currentState =
            loadingStates.get(skillId) || getInitialLoadingState();

          skillsMap.set(skillId, result.customSkill);
          loadingStates.set(skillId, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { skills: skillsMap, skillLoadingStates: loadingStates };
        });
        return result.customSkill;
      }

      set((state) => {
        const loadingStates = new Map(state.skillLoadingStates);
        const currentState =
          loadingStates.get(skillId) || getInitialLoadingState();
        loadingStates.set(skillId, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update skill",
        });
        return { skillLoadingStates: loadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.skillLoadingStates);
        const currentState =
          loadingStates.get(skillId) || getInitialLoadingState();
        loadingStates.set(skillId, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update skill",
        });
        return { skillLoadingStates: loadingStates };
      });
      return null;
    }
  },

  deleteSkill: async (skillId: string) => {
    set((state) => {
      const loadingStates = new Map(state.skillLoadingStates);
      const currentState =
        loadingStates.get(skillId) || getInitialLoadingState();
      loadingStates.set(skillId, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { skillLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`custom-skills/${skillId}`, "DELETE");

      if (response.ok && response.data) {
        DeleteCustomSkillResponseSchema.parse(response.data);
        set((state) => {
          const skillsMap = new Map(state.skills);
          const loadingStates = new Map(state.skillLoadingStates);
          skillsMap.delete(skillId);
          loadingStates.delete(skillId);
          return { skills: skillsMap, skillLoadingStates: loadingStates };
        });
        return { success: true };
      }

      const errorMessage =
        (response.data as { error?: string })?.error ||
        "Failed to delete skill";
      set((state) => {
        const loadingStates = new Map(state.skillLoadingStates);
        const currentState =
          loadingStates.get(skillId) || getInitialLoadingState();
        loadingStates.set(skillId, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        });
        return { skillLoadingStates: loadingStates };
      });
      return { success: false, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      const errorMessage = "Failed to delete skill";
      set((state) => {
        const loadingStates = new Map(state.skillLoadingStates);
        const currentState =
          loadingStates.get(skillId) || getInitialLoadingState();
        loadingStates.set(skillId, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: errorMessage,
        });
        return { skillLoadingStates: loadingStates };
      });
      return { success: false, error: errorMessage };
    }
  },
}));
